import React, { useEffect, useRef, useState } from "react";
import { CourseCategoriesResource } from "../../../stores/CourseCategoriesStore/Types";
import { CourseResource } from "../../../stores/CoursesStore/Types";

type CourseFilter = "all" | "bestseller" | "new" | "upcoming";

interface Props {
  categories: CourseCategoriesResource;
  // Deduplicated, so the chip counts do not double up courses that sit in more
  // than one skill area.
  courses: CourseResource[];
  selectedCategory: string;
  selectedFilter: CourseFilter;
  searchTerm: string;
  resultCount: number;
  onCategoryChange: (category: string) => void;
  onFilterChange: (filter: CourseFilter) => void;
  onSearchChange: (searchTerm: string) => void;
}

const categoryColor = (category, fallbackIndex) => {
  const fallbackColors = ["#3F6B5E", "#B85C3D", "#3A4A8C", "#B8893A"];

  return (
    category.label_text_color ||
    category.label_tile_color ||
    fallbackColors[fallbackIndex % fallbackColors.length]
  );
};

const CourseFilterBar: React.FC<Props> = ({
  categories,
  courses,
  selectedCategory,
  selectedFilter,
  searchTerm,
  resultCount,
  onCategoryChange,
  onFilterChange,
  onSearchChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef<HTMLDivElement>(null);
  const totalCourseCount = categories.reduce((count, category) => {
    return count + category.courses.length;
  }, 0);
  const categoryOptions = [
    {
      value: "all",
      label: "All skill areas",
      count: totalCourseCount,
      color: null,
    },
    ...categories.map((category, index) => ({
      value: category.slug,
      label: category.name,
      count: category.courses.length,
      color: categoryColor(category, index),
    })),
  ];
  const selected =
    categoryOptions.find((category) => category.value === selectedCategory) ||
    categoryOptions[0];

  const handleSelect = (category) => {
    onCategoryChange(category.value);
    setIsOpen(false);
  };

  const upcoming = (course: CourseResource) =>
    course.content_version === "upcoming";

  // The count decides whether a chip is worth showing at all — a chip with
  // nothing behind it is a dead end. It is not displayed, because these are
  // deduplicated while the grid lists a course once per skill area, and the
  // results count to the right already answers "how many".
  const filterChips = [
    { value: "all" as CourseFilter, label: "All", count: courses.filter((course) => !upcoming(course)).length },
    // No Bestsellers chip: they already have their own strip at the top of the
    // page, so a filter for them is a second route to something in view.
    {
      value: "new" as CourseFilter,
      label: "New",
      count: courses.filter(
        (course) =>
          !upcoming(course) &&
          (course.new_course || course.went_live_in_the_past_month),
      ).length,
    },
    {
      value: "upcoming" as CourseFilter,
      label: "Coming soon",
      count: courses.filter(upcoming).length,
    },
  ].filter((chip) => chip.count > 0);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      if (!selectRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  return (
    <nav className="filter-bar" aria-label="Course filters">
      <div className="container filter-bar-inner">
        <div className="filter-group">
          <span className="filter-label" id="filter-label">
            Filter:
          </span>

          <div className="custom-select" id="category-filter" ref={selectRef}>
            <button
              type="button"
              className="cs-trigger"
              aria-haspopup="listbox"
              aria-expanded={isOpen}
              aria-labelledby="filter-label cs-trigger-text"
              aria-controls="cs-menu"
              onClick={() => setIsOpen((prev) => !prev)}
            >
              <span className="cs-trigger-text" id="cs-trigger-text">
                {selected.label} ({selected.count})
              </span>

              <span className="cs-chevron" aria-hidden="true">
                <svg
                  viewBox="0 0 24 24"
                  width="14"
                  height="14"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </span>
            </button>

            <ul
              className="cs-menu"
              id="cs-menu"
              role="listbox"
              aria-labelledby="filter-label"
              tabIndex={-1}
              hidden={!isOpen}
            >
              {categoryOptions.map((category) => (
                <li
                  key={category.value}
                  role="option"
                  data-value={category.value}
                  aria-selected={selected.value === category.value}
                  className={
                    selected.value === category.value ? "is-active" : ""
                  }
                  onClick={() => handleSelect(category)}
                >
                  {category.color && (
                    <span
                      className="cs-dot"
                      style={{ ["--cs-dot" as any]: category.color }}
                    />
                  )}

                  <span className="cs-label">{category.label}</span>

                  <span className="cs-count">({category.count})</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {filterChips.length > 1 && (
          <div
            className="filter-chips"
            role="group"
            aria-label="Filter courses by type"
          >
            {filterChips.map((chip) => (
              <button
                key={chip.value}
                type="button"
                className={`filter-chip ${
                  selectedFilter === chip.value ? "is-active" : ""
                }`}
                aria-pressed={selectedFilter === chip.value}
                onClick={() => onFilterChange(chip.value)}
              >
                {chip.label}
              </button>
            ))}
          </div>
        )}

        <div className="search-wrap">
          <label htmlFor="course-search" className="sr-only">
            Search courses
          </label>

          <input
            type="search"
            id="course-search"
            className="search-input"
            placeholder="Search courses..."
            aria-label="Search courses by name or description"
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </div>

        <span
          className="filter-results-count"
          aria-live="polite"
          id="results-count"
        >
          {resultCount} {resultCount === 1 ? "course" : "courses"}
        </span>
      </div>
    </nav>
  );
};

export default CourseFilterBar;
